import * as THREE from 'three';

export type NativeAvatarMode = 'idle' | 'talk' | 'celebrate';
export const NATIVE_MOTION_SECONDS = 8;
const originals = new WeakMap<THREE.Object3D, THREE.Object3D>();
const armName = /^(Left|Right)(Shoulder|Arm|ForeArm|Hand)/;
const controlledName = /^(?:(Left|Right)(Shoulder|Arm|ForeArm|Hand)|Head$|Neck$|Neck1$|Neck2$)/;
const UP = new THREE.Vector3(0, 1, 0);
const RIGHT = new THREE.Vector3(1, 0, 0);
const FORWARD = new THREE.Vector3(0, 0, 1);
const radians = THREE.MathUtils.degToRad;

function worldRotation(node: THREE.Object3D, axis: THREE.Vector3, angle: number) {
  const parent = node.parent?.getWorldQuaternion(new THREE.Quaternion()) ?? new THREE.Quaternion();
  const delta = new THREE.Quaternion().setFromAxisAngle(axis, angle);
  const world = node.getWorldQuaternion(new THREE.Quaternion());
  node.quaternion.copy(parent.invert().multiply(delta.multiply(world))).normalize();
  node.updateWorldMatrix(false, true);
}

function initialPose(model: THREE.Object3D, embedded?: THREE.AnimationClip) {
  const cached = originals.get(model);
  if (cached) return cached.clone(true);
  const pose = model.clone(true);
  // Use only the model's authored arm/hand pose to lower its bind T-pose.
  // Sampling that model's clip at zero does not import another rig's motion.
  for (const track of embedded?.tracks ?? []) {
    if (!track.name.endsWith('.quaternion')) continue;
    const name = track.name.slice(0, -'.quaternion'.length);
    if (!armName.test(name)) continue;
    const bone = pose.getObjectByName(name);
    if (!bone || track.getValueSize() !== 4 || track.values.length < 4) continue;
    // Catalogue quaternion channels are linear; the first authored key is
    // the same value interpolation returns at the beginning of the clip.
    bone.quaternion.fromArray(track.values).normalize();
  }
  pose.updateMatrixWorld(true);
  originals.set(model, pose.clone(true));
  return pose;
}

/**
 * Original, restrained MetaPerson motion authored in this consumer.
 * The model's bone lengths/rest transforms and all facial drivers remain intact.
 * Does not emit pelvis/leg, translation, scale or morph tracks.
 */
export function buildMPNativeClip(
  model: THREE.Object3D, embeddedFirstClip: THREE.AnimationClip | undefined, mode: NativeAvatarMode,
  options: { coordinatedHead?: boolean } = {},
): THREE.AnimationClip {
  const rig = initialPose(model, embeddedFirstClip);
  const bones: THREE.Object3D[] = [];
  rig.traverse(node => { if (controlledName.test(node.name)) bones.push(node); });
  const base = new Map(bones.map(bone => [bone, bone.quaternion.clone()]));
  const times: number[] = [];
  const values = new Map(bones.map(bone => [bone, [] as number[]]));
  for (let frame = 0; frame <= 160; frame++) {
    const time = frame / 20;
    const phase = time / NATIVE_MOTION_SECONDS * Math.PI * 2;
    bones.forEach(bone => bone.quaternion.copy(base.get(bone)!));
    rig.updateMatrixWorld(true);
    const head = rig.getObjectByName('Head');
    const neck = rig.getObjectByName('Neck');
    if (neck && !options.coordinatedHead) worldRotation(neck, UP, radians(.15 * Math.sin(phase)));
    if (head && !options.coordinatedHead) {
      worldRotation(head, UP, radians(.3 * Math.sin(phase)));
      worldRotation(head, RIGHT, radians(.15 * Math.sin(phase * 2)));
    }
    for (const side of ['Left', 'Right']) {
      const shoulder = rig.getObjectByName(side + 'Shoulder');
      if (shoulder) worldRotation(shoulder, FORWARD, radians(.12 * Math.sin(phase) * (side === 'Left' ? 1 : -1)));
    }
    // The same relaxed pose is used during speech and feedback. Hands and
    // fingers retain the avatar's own lowered pose; no presentation/wave IK.
    // Only tiny shoulder breathing and head settling accompany the facial layer.
    times.push(time);
    bones.forEach(bone => {
      const array = values.get(bone)!;
      const q = bone.quaternion.clone().normalize();
      if (array.length >= 4 && q.dot(new THREE.Quaternion().fromArray(array, array.length - 4)) < 0) {
        q.set(-q.x, -q.y, -q.z, -q.w);
      }
      array.push(q.x, q.y, q.z, q.w);
    });
  }
  return new THREE.AnimationClip('vyv-native-' + mode, NATIVE_MOTION_SECONDS,
    bones.map(bone => new THREE.QuaternionKeyframeTrack(bone.name + '.quaternion', times, values.get(bone)!)));
}
